import {
	Ref,
	reactive,
	ComputedRef,
	computed,
	watch,
} from '@vue/composition-api';

export type SchemaName = string;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ValidationSchema = Record<SchemaName, FieldValidationSchema<any>>;
export type AllFields<S extends ValidationSchema> = {
	[k in keyof S]: FieldValidationState;
};

export interface FieldValidationSchema<T> {
	value: Ref<T> | ComputedRef<T>;
	rules: ValidationRule<T>[];
}

export interface ValidationRule<T> {
	validator: (newVal: T) => boolean;
}

export interface FieldValidationState {
	name: SchemaName;
	isValid: boolean;
	isDirty: boolean;
	validate(): void;
}

export interface V<S extends ValidationSchema> {
	isValid: ComputedRef<boolean>;
	isDirty: ComputedRef<boolean>;
	fields: AllFields<S>;
	validate: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function validateField(fieldSchema: FieldValidationSchema<any>): boolean {
	return fieldSchema.rules
		.every(({ validator }) => validator(fieldSchema.value.value));
}

function getFieldsFromSchema<S extends ValidationSchema>(schema: ValidationSchema): AllFields<S> {
	return Object.entries(schema)
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		.reduce((acc: AllFields<S>, curr: [ SchemaName, FieldValidationSchema<any> ]) => {
			const data = {
				name: curr[0],
				isValid: true,
				isDirty: false,
				setDirty(val: boolean) {
					this.isDirty = val;
				},
				validate() {
					data.setDirty(false);
					this.isValid = validateField(curr[1]);
				},
			};
			data.validate = data.validate.bind(data);
			data.setDirty = data.setDirty.bind(data);
			watch(curr[1].value, (currWatch, prev) => {
				if (currWatch !== prev) {
					data.setDirty(true);
				}
			});
			acc[curr[0] as keyof S] = data;
			return acc;
		}, {} as AllFields<S>);
}

export function v<S extends ValidationSchema>(schema: S): V<S> {
	const fields = reactive(getFieldsFromSchema(schema)) as AllFields<S>;
	const validate = () => {
		Object.values(fields)
			.forEach((field: FieldValidationState) => {
				// eslint-disable-next-line no-param-reassign
				field.isValid = validateField(schema[field.name]);
			});
	};
	const isValid = computed(() => Object.values(fields)
		.every((f) => f.isValid));
	const isDirty = computed(() => Object.values(fields)
		.some((f) => f.isDirty));
	return {
		isDirty,
		isValid,
		fields,
		validate,
	};
}
